import { nullishToOptional } from "../../helpers";
import { ImportWarning } from "../import/warning";
import { DataSourceAutoImportMode, DataSourceFigmaState, DataSourceRemoteType, DataSourceUploadRemoteSource, } from "./enums";
import { SourceImportStorybookSummary, SourceImportSummary, } from "./import-summary";
import { z } from "zod";
//
// Aux
//
export const DataSourceStats = z.object({
    tokens: zeroNumberByDefault(),
    components: zeroNumberByDefault(),
    assets: zeroNumberByDefault(),
    frames: zeroNumberByDefault(),
});
//
// Figma
//
export const DataSourceFigmaFileData = z.object({
    lastUpdatedAt: z.coerce.date(),
});
export const DataSourceFigmaFileVersionData = z.object({
    id: z.string(),
    label: z.string().optional(),
    description: z.string().optional(),
    createdAt: z.coerce.date(),
});
export const DataSourceFigmaScope = z.object({
    assets: z.boolean(),
    components: z.boolean(),
    documentationFrames: z.boolean(),
    tokens: z.boolean(),
    themePersistentId: z.string().optional(),
    isUnpublishedContentFallbackEnabled: z.boolean(),
});
export const DataSourceFigmaImportMetadata = z.object({
    fileData: DataSourceFigmaFileData.optional(),
    importedPublishedVersion: DataSourceFigmaFileVersionData.optional(),
});
export const DataSourceFigmaRemote = z.object({
    type: z.literal(DataSourceRemoteType.Enum.Figma),
    fileId: z.string(),
    preferredCredentialId: z.string().optional(),
    ownerId: z.string(), // todo remove or keep to reference who created data source
    ownerName: z.string(), // todo probably remove
    scope: DataSourceFigmaScope,
    state: DataSourceFigmaState,
    requiresSync: z
        .boolean()
        .optional()
        .transform((v) => v !== null && v !== void 0 ? v : false),
    lastImportMetadata: DataSourceFigmaImportMetadata.optional(),
    downloadChunkSize: z.number().optional(),
    figmaRenderChunkSize: z.number().optional(),
    maxFileDepth: z.number().optional(),
});
//
// Token Studio
//
export const DataSourceTokenStudioRemote = z.object({
    type: z.literal(DataSourceRemoteType.Enum.TokenStudio),
});
//
// Figma Variables Plugin
//
export const DataSourceUploadImportMetadata = z.record(z.any());
export const DataSourceUploadRemote = z.object({
    type: z.literal(DataSourceRemoteType.Enum.FigmaVariablesPlugin),
    remoteId: z.string(),
    remoteSourceType: DataSourceUploadRemoteSource,
    lastImportMetadata: DataSourceUploadImportMetadata.optional(),
    warnings: nullishToOptional(ImportWarning.array()),
    isTokenTypeSplitEnabled: z.boolean(),
    isCollectionsMigrationCompleted: z.boolean(),
    isCollectionKeysMigrationCompleted: z.boolean(),
});
//
// Storybook
//
export const DataSourceStorybookRemote = z.object({
    type: z.literal(DataSourceRemoteType.Enum.Storybook),
    indexUrl: z.string(),
    userUrl: z.string(),
    ownerId: z.string().optional(),
    lastImportedAt: z.coerce.date().optional(),
    lastImportedSuccessfully: z.coerce.date().optional(),
    lastImportedResult: SourceImportStorybookSummary.optional(),
    isFailed: z.boolean(),
    storiesCount: z.number(),
    docsCount: z.number(),
    description: z.string().optional(),
    isUsingVpn: z.boolean().optional(),
});
//
// Base
//
export const DataSourceRemote = z.discriminatedUnion("type", [
    DataSourceFigmaRemote,
    DataSourceUploadRemote,
    DataSourceTokenStudioRemote,
    DataSourceStorybookRemote,
]);
export const DataSource = z.object({
    id: z.string(),
    name: z.string(),
    thumbnailUrl: z.string().optional(),
    createdAt: z.coerce.date().optional(),
    lastImportedAt: z.coerce.date().optional(),
    lastImportSummary: SourceImportSummary.optional(),
    designSystemId: z.string(),
    brandPersistentId: z.string(),
    autoImportMode: DataSourceAutoImportMode,
    stats: DataSourceStats,
    remote: DataSourceRemote,
    sortOrder: z.number(),
});
export const DataSourceVersion = z.object({
    id: z.string(),
    createdAt: z.coerce.date(),
    label: z.string().nullish(),
    description: z.string().nullish(),
});
export function isDataSourceOfType(dataSource, type) {
    return dataSource.remote.type === type;
}
function zeroNumberByDefault() {
    return z
        .number()
        .nullish()
        .transform((v) => v !== null && v !== void 0 ? v : 0);
}
